import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, Textarea } from "../../shared/ui";
import type { WorkItemDetail } from "../../shared/types/api";

/**
 * The investigation record (§19).
 *
 * <p>The conclusion and the recommendation are the deliverable, which is why they get their own
 * section rather than sitting among the setup fields. A spike whose answer was never written down
 * has to be run again by the next person who asks the same question — the one outcome a
 * time-boxed investigation exists to prevent.
 */
export function SpikeDetailDrawer({
  item,
  onClose,
  onSaved,
}: {
  item: WorkItemDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [question, setQuestion] = useState("");
  const [objective, setObjective] = useState("");
  const [timeBoxHours, setTimeBoxHours] = useState("");
  const [expectedOutput, setExpectedOutput] = useState("");
  const [conclusion, setConclusion] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const spike = item?.spike;
    if (!item) {
      return;
    }
    setQuestion(spike?.question ?? "");
    setObjective(spike?.objective ?? "");
    setTimeBoxHours(spike?.timeBoxHours != null ? String(spike.timeBoxHours) : "");
    setExpectedOutput(spike?.expectedOutput ?? "");
    setConclusion(spike?.conclusion ?? "");
    setRecommendation(spike?.recommendation ?? "");
    setError(null);
  }, [item]);

  const submit = async () => {
    if (!item) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/v1/work-items/${item.id}/spike-detail`, {
        question: question || null,
        objective: objective || null,
        timeBoxHours: timeBoxHours === "" ? null : Number(timeBoxHours),
        expectedOutput: expectedOutput || null,
        conclusion: conclusion || null,
        recommendation: recommendation || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save the spike.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(item)}
      onClose={onClose}
      title="Investigation"
      subtitle={item ? `${item.itemKey} — ${item.title}` : undefined}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="The question" hint="One question. A spike that asks three is three spikes.">
          <Textarea
            rows={2}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Can we authenticate against Microsoft Entra without changing our session model?"
          />
        </Field>

        <Field label="Objective" hint="What decision this is meant to unblock.">
          <Textarea
            rows={2}
            value={objective}
            onChange={(event) => setObjective(event.target.value)}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Time box (hours)"
            hint="The point of a spike. Past this, stop and report what you have."
          >
            <Input
              type="number"
              min={0}
              step="0.5"
              value={timeBoxHours}
              onChange={(event) => setTimeBoxHours(event.target.value)}
            />
          </Field>
          <Field label="Expected output" hint="A document, a prototype, a recommendation.">
            <Input
              value={expectedOutput}
              onChange={(event) => setExpectedOutput(event.target.value)}
            />
          </Field>
        </div>

        <section className="border-t border-ink-100 pt-4">
          <h3 className="text-sm font-semibold text-ink-900">What it found</h3>
          <p className="mb-3 mt-0.5 text-sm text-ink-500">
            This is the deliverable. An investigation nobody wrote up has to be run again.
          </p>

          <div className="space-y-4">
            <Field label="Conclusion">
              <Textarea
                rows={3}
                value={conclusion}
                onChange={(event) => setConclusion(event.target.value)}
                placeholder="Yes, with one caveat: group claims arrive only on the id token."
              />
            </Field>
            <Field label="Recommendation">
              <Textarea
                rows={2}
                value={recommendation}
                onChange={(event) => setRecommendation(event.target.value)}
                placeholder="Proceed, and read groups from the id token rather than calling Graph."
              />
            </Field>
          </div>
        </section>
      </div>
    </Drawer>
  );
}
